import { useContext, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { organizerMeApi, organizerUpdateMeApi } from "../../api/api";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { handleError } from "../../helpers/handleError";
import { OrganizerHeaderContext } from "../Layout";
import "../dashboard.css";

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const Payments = () => {
  const { setPageHeader } = useContext(OrganizerHeaderContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    bankHolderName: "",
    bankHolderDocument: "",
    bankAccountType: "",
    bankAccountNumber: "",
    cbu: "",
    alias: "",
    bankName: "",
    contactEmail: "",
    identityDocumentUrl: "",
    identityDocumentName: "",
    accountProofUrl: "",
    accountProofName: "",
    additionalNotes: "",
    acceptedTerms: false,
    acceptedOwnershipDeclaration: false,
    acceptedDataDeclaration: false,
  });
  const [selectedFiles, setSelectedFiles] = useState({
    identityDocumentName: "",
    accountProofName: "",
  });

  useEffect(() => {
    setPageHeader({
      title: "Informacion de pagos",
      subtitle: "Completa y envia tu validacion para poder vender entradas pagas.",
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await organizerMeApi();
      const nextProfile = res?.data?.info || null;
      const payment = nextProfile?.paymentValidation || {};
      setProfile(nextProfile);
      setForm({
        bankHolderName: payment.bankHolderName || "",
        bankHolderDocument: payment.bankHolderDocument || "",
        bankAccountType: payment.bankAccountType || "",
        bankAccountNumber: payment.bankAccountNumber || "",
        cbu: payment.cbu || "",
        alias: payment.alias || "",
        bankName: payment.bankName || "",
        contactEmail: payment.contactEmail || nextProfile?.email || "",
        identityDocumentUrl: payment.identityDocumentUrl || "",
        identityDocumentName: payment.identityDocumentName || "",
        accountProofUrl: payment.accountProofUrl || "",
        accountProofName: payment.accountProofName || "",
        additionalNotes: payment.additionalNotes || "",
        acceptedTerms: Boolean(payment.acceptedTerms),
        acceptedOwnershipDeclaration: Boolean(payment.acceptedOwnershipDeclaration),
        acceptedDataDeclaration: Boolean(payment.acceptedDataDeclaration),
      });
      setSelectedFiles({
        identityDocumentName: payment.identityDocumentName || "",
        accountProofName: payment.accountProofName || "",
      });
    } catch (error) {
      handleError(error);
      toast.error("No se pudo cargar la informacion de pagos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onFileChange = async (event) => {
    const { name, files } = event.target;
    const file = files?.[0] || null;
    if (!file) return;

    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (name === "identityDocumentFile") {
        setForm((prev) => ({
          ...prev,
          identityDocumentUrl: dataUrl,
          identityDocumentName: file.name,
        }));
        setSelectedFiles((prev) => ({ ...prev, identityDocumentName: file.name }));
      }
      if (name === "accountProofFile") {
        setForm((prev) => ({
          ...prev,
          accountProofUrl: dataUrl,
          accountProofName: file.name,
        }));
        setSelectedFiles((prev) => ({ ...prev, accountProofName: file.name }));
      }
    } catch (_) {
      toast.error("No se pudo leer el archivo seleccionado.");
    }
  };

  const saveDraft = async () => {
    try {
      setSaving(true);
      await organizerUpdateMeApi({ paymentValidation: form });
      toast.success("Borrador guardado.");
      await loadProfile();
    } catch (error) {
      handleError(error);
      toast.error(error?.response?.data?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const submitForReview = async () => {
    try {
      setSaving(true);
      await organizerUpdateMeApi({
        paymentValidation: form,
        submitPaymentValidation: true,
      });
      toast.success("Tu validacion de pagos fue enviada a revision.");
      await loadProfile();
    } catch (error) {
      handleError(error);
      toast.error(error?.response?.data?.message || "No se pudo enviar la solicitud.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SpinLoading />;

  const capability = profile?.capability || {};
  const payment = profile?.paymentValidation || {};
  const isApproved = payment.status === "APPROVED";
  const isInReview = payment.status === "IN_REVIEW";

  return (
    <div className="organizer-page organizer-payments">
      <section className="organizer-section">
        <div className="organizer-section__header">
          <div>
            <p className="organizer-eyebrow">Pagos</p>
            <h2>Informacion de pagos</h2>
            <p className="organizer-section__subtitle">
              {capability.message || "Completa este modulo para habilitar cobros y liquidaciones."}
            </p>
          </div>
          <span
            className={`organizer-chip ${
              isApproved ? "organizer-chip--success" : "organizer-chip--secondary"
            }`}
          >
            {capability.label || "Sin estado"}
          </span>
        </div>

        {payment.rejectionReason ? (
          <div className="organizer-empty-card" style={{ marginBottom: 16 }}>
            <strong>Observacion de ZOCO:</strong> {payment.rejectionReason}
          </div>
        ) : null}

        <div className="organizer-form-grid organizer-form-grid--two">
          <div className="organizer-form-card organizer-event-form">
            <h3>Cuenta receptora</h3>
            <Form>
              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Titular de la cuenta</Form.Label>
                <Form.Control
                  className="organizer-field-input"
                  name="bankHolderName"
                  value={form.bankHolderName}
                  onChange={onChange}
                />
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">DNI / CUIT / CUIL</Form.Label>
                <Form.Control
                  className="organizer-field-input"
                  name="bankHolderDocument"
                  value={form.bankHolderDocument}
                  onChange={onChange}
                />
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Tipo de cuenta</Form.Label>
                <Form.Control
                  className="organizer-field-input"
                  name="bankAccountType"
                  value={form.bankAccountType}
                  onChange={onChange}
                />
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Numero de cuenta</Form.Label>
                <Form.Control
                  className="organizer-field-input"
                  name="bankAccountNumber"
                  value={form.bankAccountNumber}
                  onChange={onChange}
                />
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">CBU / CVU</Form.Label>
                <Form.Control
                  className="organizer-field-input"
                  name="cbu"
                  value={form.cbu}
                  onChange={onChange}
                />
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Alias</Form.Label>
                <Form.Control
                  className="organizer-field-input"
                  name="alias"
                  value={form.alias}
                  onChange={onChange}
                />
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Banco / PSP</Form.Label>
                <Form.Control
                  className="organizer-field-input"
                  name="bankName"
                  value={form.bankName}
                  onChange={onChange}
                />
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Email de contacto</Form.Label>
                <Form.Control
                  className="organizer-field-input"
                  name="contactEmail"
                  value={form.contactEmail}
                  onChange={onChange}
                />
              </Form.Group>
            </Form>
          </div>

          <div className="organizer-form-card organizer-event-form">
            <h3>Documentacion y declaraciones</h3>
            <Form>
              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Documento de identidad</Form.Label>
                <Form.Control
                  className="organizer-field-input organizer-file-input"
                  type="file"
                  name="identityDocumentFile"
                  accept=".pdf,image/*"
                  onChange={onFileChange}
                />
                {selectedFiles.identityDocumentName ? (
                  <p className="organizer-field-hint">
                    Archivo cargado: {selectedFiles.identityDocumentName}
                  </p>
                ) : null}
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Constancia bancaria</Form.Label>
                <Form.Control
                  className="organizer-field-input organizer-file-input"
                  type="file"
                  name="accountProofFile"
                  accept=".pdf,image/*"
                  onChange={onFileChange}
                />
                {selectedFiles.accountProofName ? (
                  <p className="organizer-field-hint">
                    Archivo cargado: {selectedFiles.accountProofName}
                  </p>
                ) : null}
              </Form.Group>

              <Form.Group className="organizer-field-block">
                <Form.Label className="organizer-field-label">Notas adicionales</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  className="organizer-field-input organizer-textarea"
                  name="additionalNotes"
                  value={form.additionalNotes}
                  onChange={onChange}
                />
              </Form.Group>

              <Form.Check
                className="mb-3 organizer-payments__check"
                type="checkbox"
                label="Declaro que la cuenta informada pertenece al organizador registrado."
                name="acceptedOwnershipDeclaration"
                checked={form.acceptedOwnershipDeclaration}
                onChange={onChange}
              />
              <Form.Check
                className="mb-3 organizer-payments__check"
                type="checkbox"
                label="Declaro que los datos bancarios son verdaderos y vigentes."
                name="acceptedDataDeclaration"
                checked={form.acceptedDataDeclaration}
                onChange={onChange}
              />
              <Form.Check
                className="mb-3 organizer-payments__check"
                type="checkbox"
                name="acceptedTerms"
                checked={form.acceptedTerms}
                onChange={onChange}
                label={
                  <span>
                    Acepto los{" "}
                    <a
                      href="https://organizador.zocotickets.com/terminos-y-condiciones-organizador"
                      target="_blank"
                      rel="noreferrer"
                      className="organizer-inline-link"
                    >
                      terminos de ZOCO Pagos
                    </a>.
                  </span>
                }
              />
            </Form>

            <div className="organizer-form-actions">
              <button
                type="button"
                className="organizer-btn secondary"
                onClick={saveDraft}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Guardar borrador"}
              </button>
              <button
                type="button"
                className="organizer-btn"
                onClick={submitForReview}
                disabled={saving || isInReview}
              >
                {isInReview ? "En revision" : "Enviar a revision"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Payments;
